import { appendProbationTimelineEvents } from './probationTimelineUtils';
import { isMilestoneLetterCode } from '../hooks/useTimelineData';
import { buildMovementDiffs } from './employmentMovementDiffs';

/**
 * Build a sorted career-timeline event list for an employee (admin / self-service).
 *
 * @param {object} opts
 * @param {object} opts.employee
 * @param {object[]} [opts.hrLetters]
 * @param {object[]} [opts.certificates]
 * @param {object[]} [opts.trainingEnrollments]
 * @param {object[]} [opts.educationRecords]
 * @param {object[]} [opts.employmentMovements]
 * @param {'admin'|'employee'} [opts.audience='admin']
 * @returns {object[]}
 */
export function buildCareerTimelineEvents({
  employee,
  hrLetters = [],
  certificates = [],
  trainingEnrollments = [],
  educationRecords = [],
  employmentMovements = [],
  audience = 'admin',
} = {}) {
  const events = [];
  const empStart = employee?.employment_start_date;
  const empStartYear = empStart ? new Date(empStart).getFullYear() : null;

  if (empStart) {
    events.push({
      date: empStart,
      title: 'Joined Company',
      type: 'system',
      subtitle: 'Career milestone',
    });
  }

  appendProbationTimelineEvents(events, {
    probEndDate: employee?.hrprofile_probation_end_date,
    isProbation: employee?.employment_status === 'probation',
    probationExtended: !!employee?.probation_extended,
    extensionReason: employee?.probation_extension_reason,
    probationStartDate: employee?.probation_start_date || empStart,
    audience,
  });

  if (employee?.employment_end_date) {
    events.push({
      date: employee.employment_end_date,
      title: 'Left Company',
      type: 'system',
      subtitle: 'Career milestone',
    });
  }

  hrLetters.forEach((letter) => {
    if (!isMilestoneLetterCode(letter.template_code)) return;
    events.push({
      date: letter.generated_at?.split('T')[0] || letter.generated_at,
      title: letter.letter_type_display || letter.template_code,
      type: 'hr_letter',
      subtitle: letter.letter_reference || 'HR letter issued',
    });
  });

  if (empStart) {
    certificates.forEach((cert) => {
      if (!cert.cert_issue_date) return;
      if (cert.cert_issue_date < empStart) return;
      events.push({
        date: cert.cert_issue_date,
        title: cert.cert_name,
        type: 'certificate',
        subtitle: cert.cert_issuing_organization || 'Professional certificate',
      });
    });
  }

  trainingEnrollments.forEach((enrollment) => {
    if (!enrollment.completion_date) return;
    const courseTitle = enrollment.course_detail?.course_title || 'Training course';
    events.push({
      date: enrollment.completion_date,
      title: courseTitle,
      type: 'training',
      subtitle: enrollment.certification_issued
        ? 'Completed — certification issued'
        : 'Course completed',
    });
  });

  const empStartKey = empStart ? String(empStart).slice(0, 10) : null;
  employmentMovements.forEach((m) => {
    if (m.change_type === 'initial') return;
    const effectiveKey = m.effective_from ? String(m.effective_from).slice(0, 10) : null;
    if (empStartKey && effectiveKey === empStartKey) return;
    if (m.change_type === 'correction') return;
    // Multi-field transfers are unreadable as one middot string — stack labeled
    // diffs (same shape as Employment History table). Salary stays table-only.
    const details = buildMovementDiffs(m, { includeSalary: false });
    events.push({
      date: m.effective_from,
      title: m.is_scheduled
        ? `${m.change_type_display} (Scheduled)`
        : m.change_type_display,
      type: m.is_scheduled ? 'upcoming' : 'movement',
      details,
      // Fallback only when there are no field diffs (reason-only / empty change).
      subtitle: details.length ? null : (m.change_reason || 'Employment change'),
    });
  });

  if (empStartYear) {
    educationRecords.forEach((edu) => {
      if (!edu.education_end_year) return;
      if (edu.education_is_ongoing) return;
      if (edu.education_end_year < empStartYear) return;
      events.push({
        date: `${edu.education_end_year}-12-31`,
        title: edu.education_qualification_name || 'Education',
        type: 'education',
        subtitle: edu.institution_display_name || edu.education_field_of_study || 'Education completed',
        yearOnly: true,
      });
    });
  }

  return events.filter((e) => e.date).sort((a, b) => new Date(b.date) - new Date(a.date));
}

export const CAREER_TIMELINE_EVENT_STYLES = {
  system: { dot: 'bg-blue-600', label: 'text-gray-600' },
  milestone: { dot: 'bg-green-500', label: 'text-green-700' },
  upcoming: { dot: 'bg-amber-400 border-dashed', label: 'text-amber-700' },
  overdue: { dot: 'bg-red-500', label: 'text-red-700' },
  extended: { dot: 'bg-blue-500', label: 'text-blue-700' },
  hr_letter: { dot: 'bg-indigo-500', label: 'text-indigo-700' },
  certificate: { dot: 'bg-amber-500', label: 'text-amber-700' },
  training: { dot: 'bg-teal-500', label: 'text-teal-700' },
  education: { dot: 'bg-purple-500', label: 'text-purple-700' },
  movement: { dot: 'bg-emerald-500', label: 'text-emerald-700' },
};
